import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { orgApi } from '../features/org/api/org.api.js';
import { orgKeys } from '../features/org/model/org.queries.js';
import { warehouseApi } from '../features/warehouse/api/warehouse.api.js';
import type { ScopeKind, ScopeOption } from '../features/rbac/ui/ScopePicker.js';

/**
 * DOIRA OBYEKTLARI — ILOVA QATLAMIDA yig'iladi.
 *
 * `features/rbac` `features/org` ni ham, `features/warehouse` ni ham
 * BILMAYDI: doira tanlagich uchun ular shunchaki `ScopeOption` ro'yxati.
 * Uchalasini bir-biriga shu hook bog'laydi.
 *
 * Ro'yxat TO'LIQ yuklanadi, sahifalab emas. Sabab: u ikki ishni bajaradi —
 * tanlagichni to'ldiradi VA mavjud biriktiruvlarning nomini topib beradi
 * ("MTU: Qarshi..."). Yarim yuklangan ro'yxatda ikkinchisi jimgina buziladi
 * va ekranda nom o'rniga "#23" chiqib qoladi.
 */

/** Bitta sahifadagi eng katta hajm — shartnomadagi chegara (`paginationQuerySchema`). */
const PAGE = 200;

/** Stansiya ham `ORG_UNIT`, lekin ekranda alohida toifa. */
const kindOfUnit = (type: string): ScopeKind =>
  type === 'MTU' ? 'MTU' : type === 'STATION' ? 'STATION' : 'ORG_UNIT';

/**
 * `LINE` (yo'nalish) doira sifatida BERILMAYDI: u ma'muriy birlik emas,
 * stansiyalarni guruhlash uchun turadi va unda na xodim, na ombor bo'ladi.
 */
const SELECTABLE = (type: string): boolean => type !== 'LINE';

/*
 * Sahifalash `orgApi.listAllUnits` da — BU YERDA takrorlanmaydi.
 *
 * Ilgari o'sha tsikl shu yerda ikkinchi marta yozilgan edi va u bitta
 * korxonani ikki sahifada qabul qilardi: server tartibi bir xil nomli
 * yozuvlarni kafolatlamaydi. Tanlagichda bu bir xil nomli ikki qator bo'lib
 * chiqardi. Endi ikkala chaqiruvchi ham bitta, `id` bo'yicha noyoblashtirgan
 * funksiyadan foydalanadi.
 */
const useAllOrgUnits = () =>
  useQuery({
    queryKey: orgKeys.unitsFlat(),
    queryFn: () => orgApi.listAllUnits(),
    staleTime: 300_000,
  });

export const useScopeOptions = (): ScopeOption[] => {
  const units = useAllOrgUnits();

  const warehouses = useQuery({
    queryKey: ['warehouse', 'list', { limit: PAGE }],
    queryFn: () => warehouseApi.list({ limit: PAGE }),
    staleTime: 300_000,
  });

  return useMemo(() => {
    const unitOptions: ScopeOption[] = (units.data ?? [])
      .filter((unit) => SELECTABLE(unit.type))
      .map((unit) => ({
        id: unit.id,
        label: unit.name,
        kind: kindOfUnit(unit.type),
        // MTU ning o'zi uchun `mtuId` — o'z identifikatori: shunda u
        // tanlangan MTU ro'yxatida ham, obyekt filtrida ham izchil ishlaydi.
        mtuId: unit.type === 'MTU' ? unit.id : unit.mtuId,
        /*
         * Belgi sifatida KOD, `shortName` emas.
         *
         * `short_name` NOYOB EMAS: bazada 516 ta korxonaning 401 ta har xil
         * qisqa nomi bor va «Qarshi mintaqaviy temir yo'l uzeli» ning
         * ikkala qatorida ham u «MTU-5». Ya'ni qisqa nom bilan ular
         * ekranda AYNAN bir xil ko'rinardi. `code` esa noyob (516/516) va
         * ikkisini har doim ajratadi: «MTU-5» va «MTU-5-2».
         */
        hint: unit.code,
      }));

    const warehouseOptions: ScopeOption[] = (warehouses.data?.items ?? []).map((warehouse) => ({
      id: warehouse.id,
      label: warehouse.name,
      kind: 'WAREHOUSE' as const,
      mtuId: warehouse.mtuId,
      hint: warehouse.code,
    }));

    return [...unitOptions, ...warehouseOptions];
  }, [units.data, warehouses.data]);
};
